import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import ClassDataContents from "../../../templates/teacher/ClassDataContents";
import {
  fetchPackages,
  fetchSurveyAnswers,
  fetchDetailsSurvey,
  classDataPath,
  fetchSimpleTeachingMaterials,
  fetchListLessons,
  fetchPortfolio,
  fetchTemporaryToken,
  clearTemporaryToken,
  fetchStudents,
  fetchClasses,
  resetStudentPassword,
} from "../../../teacherStore";
import { Context } from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import { parseQueryParams } from "../../../libs/Util";

export type IClassDataScreenType = "response" | "portfolio" | "survey";

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const [materialCalling, materialPeep] = useWaitApiCall(state);
  const history = useHistory();
  const { pathname } = useLocation();
  const params = useParams<{
    class_id: string;
    student_id: string;
  }>();
  const student_id = parseInt(params.student_id);
  const class_id = parseInt(params.class_id);

  const currentStudent = React.useMemo(() => {
    return state.students.find(s => s.id === student_id);
  }, [state.students, student_id]);

  const nameClass = React.useMemo(() => {
    return state.classes.find(c => c.id === class_id)?.name ?? "";
  }, [state.classes, class_id]);

  const screenType = pathname.split("/").pop() as IClassDataScreenType;
  const links = [
    {
      name: `クラスデータ`,
      to: classDataPath,
    },
    {
      name: nameClass ?? "",
      to: `${classDataPath}/${class_id}`,
      state: { nameClass },
    },
  ];

  const teachingMaterialId = Number(parseQueryParams()["teaching_material_id"]);

  React.useEffect(() => {
    if (state.students.length === 0) {
      dispatch(fetchStudents([class_id]));
    }
  }, [dispatch, state.students, class_id]);
  React.useEffect(() => {
    if (state.classes.length === 0) {
      dispatch(fetchClasses());
    }
  }, [dispatch, state.classes]);

  React.useEffect(() => {
    dispatch(materialPeep(fetchSimpleTeachingMaterials(class_id)));
  }, [dispatch, materialPeep, class_id]);

  React.useEffect(() => {
    if (screenType === "survey") {
      dispatch(fetchPackages(class_id));
    }
  }, [dispatch, class_id, screenType]);

  const fetchSurveyFunc = React.useCallback(
    (material_id: number) => {
      dispatch(peep(fetchSurveyAnswers(material_id, student_id)));
    },
    [dispatch, student_id, peep],
  );

  const fetchDetailsSurveyFunc = React.useCallback(
    (package_id: number) => {
      dispatch(peep(fetchDetailsSurvey(student_id, package_id)));
    },
    [dispatch, peep, student_id],
  );

  const onBackFunc = React.useCallback(
    () =>
      history.push(classDataPath, {
        nameClass,
      }),
    [history, nameClass],
  );

  const changeUrl = React.useCallback(
    (newStudentId: number, newTeachingMaterialId: number) => {
      history.push(
        `${classDataPath}/${class_id}/student/${newStudentId}/${screenType}?teaching_material_id=${newTeachingMaterialId}`,
      );
    },
    [history, class_id, screenType],
  );

  const onTeachingMaterialOfPortfolioChangeFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(fetchPortfolio(student_id, class_id, id)));
    },
    [class_id, dispatch, peep, student_id],
  );

  const onTeachingMaterialOfQuestionChangeFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(fetchListLessons(student_id, class_id, id)));
      changeUrl(student_id, id);
    },
    [class_id, dispatch, peep, student_id, changeUrl],
  );

  const handleDownloadFile = React.useCallback(() => {
    dispatch(peep(fetchTemporaryToken()));
  }, [dispatch, peep]);

  const handleResetStudentPassword = React.useCallback(() => {
    if (!currentStudent) return;
    dispatch(peep(resetStudentPassword(currentStudent.id)));
  }, [dispatch, peep, currentStudent]);

  const handleClearTemporaryToken = React.useCallback(() => {
    dispatch(clearTemporaryToken());
  }, [dispatch]);

  const handleChangeStudent = React.useCallback(
    student => {
      if (!student) return;
      changeUrl(student.id, teachingMaterialId);
    },
    [teachingMaterialId, changeUrl],
  );

  return (
    <ClassDataContents
      calling={calling}
      materialCalling={materialCalling}
      screenType={screenType}
      links={links}
      student={currentStudent}
      students={state.students}
      nameClass={nameClass}
      surveyEnabled={state.survey_enabled}
      details_survey={state.details_survey}
      packages={state.packages}
      survey_answers={state.survey_answers}
      survey_answers_per_teaching_material={
        state.survey_answers_per_teaching_material
      }
      teaching_materials={state.simple_teaching_materials}
      lessonsByTeachingMaterial={state.lessonsByTeachingMaterial}
      portfolio={state.portfolio}
      temporaryToken={state.temporaryToken}
      teachingMaterialId={teachingMaterialId}
      onBack={onBackFunc}
      fetchSurveyFunc={fetchSurveyFunc}
      fetchDetailsSurveyFunc={fetchDetailsSurveyFunc}
      onTeachingMaterialOfPortfolioChange={
        onTeachingMaterialOfPortfolioChangeFunc
      }
      onTeachingMaterialOfQuestionChange={
        onTeachingMaterialOfQuestionChangeFunc
      }
      onDownloadFile={handleDownloadFile}
      onClearTemporaryToken={handleClearTemporaryToken}
      onChangeStudent={handleChangeStudent}
      onReissueStudentPassword={handleResetStudentPassword}
    />
  );
};
